'use client';

/**
 * 2026-07-08 R95 wave 4 (task #265) · Inbox notifications bell.
 *
 * Sits next to ProfileMenu on desktop and inside the mobile chrome.
 * Polls /me/notifications every 30s while the tab is focused; clicking
 * opens a drawer that shows the recent 50. Unread rows get a warm
 * accent + a subtle gold dot; opening the drawer + clicking a row
 * marks the row read (POST /:id/read), and 全部标为已读 flushes the
 * unread queue in one call.
 *
 * Intentionally minimal — no SSE, no real-time push. The 30s poll is
 * good enough for the "distill finished, come review" moment which is
 * the only thing that populates the inbox today.
 */

import { formatRelativeTime } from '@/lib/format-date';
import { Bell } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

type Notification = {
  id: string;
  type: string;
  title: string;
  body: string | null;
  ref_type: string | null;
  ref_id: string | null;
  action_url: string | null;
  created_at: string;
  read_at: string | null;
};

const POLL_MS = 30_000;

export function NotificationBell() {
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  // 2026-07-14 (Leon: 提炼完成后前端要自动展示) — the bell already
  // polls every 30s; when a NEW notification lands (a background
  // distill finished), broadcast the vault-counts invalidate event so
  // VaultSubNav badges + PendingDistillBanner refresh without the user
  // reloading. Tracks the latest seen notification id (not unread
  // count, which also moves on mark-read).
  const latestSeenIdRef = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await fetch('/api/me/notifications', {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!r.ok || cancelled) return;
        const body = (await r.json()) as {
          notifications: Notification[];
          unread_count: number;
        };
        setItems(body.notifications ?? []);
        setUnread(body.unread_count ?? 0);
        const newestId = body.notifications?.[0]?.id ?? null;
        if (latestSeenIdRef.current !== null && newestId && newestId !== latestSeenIdRef.current) {
          window.dispatchEvent(new Event('agentx:vault-counts-invalidate'));
        }
        latestSeenIdRef.current = newestId ?? latestSeenIdRef.current;
      } catch {
        // Silent — notifications are non-blocking.
      }
    }
    void load();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, POLL_MS);
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  async function markRead(id: string) {
    setItems((prev) =>
      prev.map((n) =>
        n.id === id && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n,
      ),
    );
    setUnread((u) => Math.max(0, u - 1));
    try {
      await fetch(`/api/me/notifications/${encodeURIComponent(id)}/read`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // best-effort; next poll reconciles
    }
  }

  async function markAllRead() {
    setItems((prev) =>
      prev.map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() })),
    );
    setUnread(0);
    try {
      await fetch('/api/me/notifications/read-all', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // best-effort
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`通知${unread > 0 ? ` (${unread} 未读)` : ''}`}
        className="relative w-8 h-8 rounded-full hover:bg-paper-3 flex items-center justify-center text-ink-mute hover:text-ink transition"
      >
        {/* 2026-07-09 R95 wave 15d (Leon 反馈) — emoji 🔔 各操作系统渲染
            不一致 (macOS 立体, Windows 平, Linux 灰); Amy 设计系统里
            所有 inline icon 都走 lucide. 换成 lucide Bell 保持一致. */}
        <Bell aria-hidden size={16} strokeWidth={1.75} />
        {unread > 0 && (
          <span
            aria-hidden
            className="absolute top-0 right-0 min-w-[16px] h-4 px-1 rounded-full bg-warmth-dark text-paper text-[10px] font-mono font-bold flex items-center justify-center"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label="关闭通知面板"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-full mt-2 z-50 w-[360px] max-w-[calc(100vw-1rem)] max-h-[70vh] flex flex-col rounded-r-md border border-line bg-white shadow-xl">
            <header className="flex items-center gap-3 px-4 py-3 border-b border-line-soft">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-gold-3">INBOX</p>
              <span className="text-ink-mute text-xs">
                {unread > 0 ? `${unread} 未读` : '全部已读'}
              </span>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => void markAllRead()}
                  className="ml-auto text-[11px] text-ink-light hover:text-gold-3 underline underline-offset-2 transition"
                >
                  全部标为已读
                </button>
              )}
            </header>
            <div className="flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <div className="py-10 text-center text-ink-mute text-xs">
                  <p>还没有通知。</p>
                  <p className="mt-1">当 AI 提炼完成会在这里出现。</p>
                </div>
              ) : (
                <ul className="divide-y divide-line-soft">
                  {items.map((n) => {
                    const unreadRow = !n.read_at;
                    const inner = (
                      <div
                        className={`px-4 py-3 transition ${
                          unreadRow ? 'bg-gold-3/5 hover:bg-gold-3/10' : 'hover:bg-paper-3'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {unreadRow && (
                            <span
                              aria-hidden
                              className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-gold-3"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-ink font-medium leading-snug">{n.title}</p>
                            {n.body && (
                              <p className="text-xs text-ink-mute leading-relaxed mt-1">{n.body}</p>
                            )}
                            <p className="text-[10px] text-ink-light font-mono mt-1.5">
                              {formatRelativeTime(n.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                    return (
                      <li key={n.id}>
                        {n.action_url ? (
                          <Link
                            href={n.action_url}
                            onClick={() => {
                              setOpen(false);
                              if (unreadRow) void markRead(n.id);
                            }}
                            className="block"
                          >
                            {inner}
                          </Link>
                        ) : (
                          <button
                            type="button"
                            onClick={() => unreadRow && void markRead(n.id)}
                            className="block w-full text-left"
                          >
                            {inner}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
